import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { IoInformationCircleOutline } from 'react-icons/io5';
import CompanyDetailsOverlay from './company-details';

const LoadingOverlay = ({ loading, progress, isFetchingData }) => {
  if (!loading) return null;

  return (
      <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/90 flex flex-col items-center justify-center z-50"
      >
          <div className="w-96 flex flex-col items-center gap-8">
              {progress < 100 ? (
                  // **Progress Bar Based Loader**
                  <div className="relative h-32 w-32">
                      <svg className="absolute inset-0" viewBox="0 0 100 100">
                          <circle
                              className="stroke-gray-200"
                              strokeWidth="8"
                              fill="transparent"
                              r="44"
                              cx="50"
                              cy="50"
                          />
                          <circle
                              className="transition-all duration-300"
                              strokeWidth="8"
                              fill="transparent"
                              r="44"
                              cx="50"
                              cy="50"
                              strokeDasharray="276.46"
                              strokeDashoffset={276.46 * (1 - progress / 100)}
                              strokeLinecap="round"
                              transform="rotate(-90 50 50)"
                              stroke="#FC8F41"
                          />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-2xl font-semibold">{Math.round(progress)}%</span>
                      </div>
                  </div>
              ) : (
                  // **Circular Spinning Loader When Data Fetch is Pending**
                  <div className="relative h-20 w-20 flex items-center justify-center">
                      <div className="absolute w-full h-full border-4 border-gray-300 rounded-full animate-spin border-t-orng"></div>
                  </div>
              )}

              <div className="text-center space-y-2">
                  <p className="text-lg font-semibold text-gray-800">
                      {progress < 100 ? 'Loading Competitor Analysis' : 'Finalizing Data'}
                  </p>
                  <p className="text-sm text-gray-600">
                      {progress < 100
                          ? "Fetching competitor data for the first time. Subsequent loads will be faster!"
                          : "Data is almost ready. Just a few moments more!"}
                  </p>
              </div>
          </div>
      </motion.div>
  );
};

  

const CompetitorCard = ({ competitor, baseUrl }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const handleCardClick = () => {
    setShowDetails(true);
    setIsLoading(true);
    setLoadingProgress(0);
    
    // Simulate loading progress
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsLoading(false);
          return 100;
        }
        return prev + 1;
      });
    }, 150);
  };

  return (
    <>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="group bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer relative"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={handleCardClick}
      >
        <div className="bg-orng p-4 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white truncate">
            {competitor.name}
          </h2>
          <a
            href={competitor.link}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white orng px-4 py-1 rounded-full text-sm font-medium hover:bg-orng transition-colors whitespace-nowrap flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            Visit →
          </a>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-700 leading-relaxed">
            {competitor.description}
          </p>

          {/* Info Banner */}
          <div className="flex items-center gap-2 text-sm text-gray-500 group-hover:text-orng transition-colors">
            <IoInformationCircleOutline className="w-5 h-5" />
            <p>Click to view more</p>
          </div>

          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{
              opacity: isHovered ? 1 : 0,
              height: isHovered ? 'auto' : 0,
            }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {competitor.citations && competitor.citations.length > 0 && (
              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm font-medium text-gray-500 mb-2">Sources:</p>
                <div className="space-y-1">
                  {competitor.citations.map((citation, index) => (
                    <a
                      key={index}
                      href={citation}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm text-blue-600 hover:text-blue-800 hover:underline truncate"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {new URL(citation).hostname}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>

      {showDetails &&
        createPortal(
          <CompanyDetailsOverlay
            companyId={competitor.id}
            onClose={() => setShowDetails(false)}
            baseUrl={baseUrl}
            companyName={competitor.name}
          />,
          document.body
        )}
    </>
  );
};

const CompetitorAnalysis = () => {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const base_url = process.env.REACT_APP_BASE_URL;
  const workspaceId = Cookies.get('workspaceId');
  const [isFetchingData, setIsFetchingData] = useState(true);

  useEffect(() => {
    const fetchCompetitors = async () => {
        try {
            if (!workspaceId) {
                throw new Error('No workspace ID found');
            }

            const progressInterval = setInterval(() => {
                setProgress((prev) => (prev < 95 ? prev + 10 : prev));
            }, 300);

            const response = await axios.get(`${base_url}/get-competitor-analysis-screen`, {
                headers: { workspaceId },
            });

            clearInterval(progressInterval);
            setCompetitors(response.data.competitors);
            setProgress(100);

            setTimeout(() => setIsFetchingData(false), 2000); // Delay to show circular loader briefly
        } catch (err) {
            console.error('Error fetching competitor analysis:', err);
            setError('Failed to fetch competitor analysis. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    fetchCompetitors();
}, [base_url, workspaceId]);

  if (!workspaceId) {
    window.location.href = '/';
    return null;
  }

  if (loading) {
    return <LoadingOverlay loading={loading} progress={progress} />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 p-8"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-12">
          <div>
            <h1 className="text-5xl md:text-6xl font-semibold uppercase leading-snug tracking-wide">
              Competitor Analysis
            </h1>
            <p className="text-gray-600 mt-2">
              Overview of key players in your market space
            </p>
          </div>
          <button
            onClick={() => window.location.href = '/overview'}
            className="bg-orng hover:bg-orange-600 text-white py-2 px-6 rounded-full transition-colors"
          >
            Back to Overview
          </button>
        </div>

        <AnimatePresence>
          {competitors.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <p className="text-xl text-gray-600">No competitors found for analysis.</p>
            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {competitors.map((competitor, index) => (
                <motion.div
                  key={competitor.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <CompetitorCard 
                    competitor={competitor}
                    baseUrl={base_url}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default CompetitorAnalysis;